import { ZOHO_BOOKS_IMPORT_PATH, ZOHO_BOOKS_SETTINGS_ROUTE } from './importRun';
import { isZohoBooksDataCentre, zohoBooksHosts, type ZohoBooksDataCentre } from './zohoBooksSettings';
import type { SealedValueView } from '../settings/maps';

/**
 * Connect with Zoho (piece 15, piece 31; decision 1465, amending 0922): the
 * Server-based client's round trip. Settings › Integrations › Zoho Books asks
 * the connect route for Zoho's authorization address, the browser goes there,
 * and Zoho sends it back to the callback route with a code the API exchanges
 * for the refresh token. 🔨 The refresh token never reaches a browser.
 *
 * Every parameter name is Zoho's own, from https://www.zoho.com/books/api/v3/oauth/.
 */

/** 🔒 The seventeen read scopes the consent page asks for, held once (decisions 1465, 3340, 3660, 3661, 4800): fifteen of Zoho Books and two of Zoho Inventory. */
export const ZOHO_BOOKS_SCOPES: readonly string[] = [
  'ZohoBooks.contacts.READ',
  'ZohoBooks.settings.READ',
  'ZohoBooks.invoices.READ',
  'ZohoBooks.creditnotes.READ',
  'ZohoBooks.customerpayments.READ',
  'ZohoBooks.bills.READ',
  'ZohoBooks.debitnotes.READ',
  'ZohoBooks.vendorpayments.READ',
  'ZohoBooks.expenses.READ',
  'ZohoBooks.accountants.READ',
  // 🔒 Decision 2141: the opening balances read Zoho's balance sheet, which a token without this scope is refused.
  'ZohoBooks.reports.READ',
  // 🔒 Decision 3340: block 4 reads the estimates, the sales orders and the purchase orders, which a token without these is refused.
  'ZohoBooks.estimates.READ',
  'ZohoBooks.salesorders.READ',
  'ZohoBooks.purchaseorders.READ',
  // 🔒 Decision 3660: a documents read reads Zoho Inventory's adjustments, under Inventory's own scope.
  'ZohoInventory.inventoryadjustments.READ',
  // 🔒 Decision 3661: an items read reads each item's picture from Zoho Inventory, under its items scope.
  'ZohoInventory.items.READ',
  // 🔒 Decision 4800: block 1 reads the bank and credit card accounts Zoho keeps apart from the chart, under the banking scope.
  'ZohoBooks.banking.READ',
];

/** How long a minted `state` is honoured, beside single sign-on's ten minutes. */
export const ZOHO_BOOKS_STATE_MINUTES = 10;

export const ZOHO_BOOKS_CONNECT_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/connect`;
export const ZOHO_BOOKS_CALLBACK_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/callback`;
export const ZOHO_BOOKS_DISCONNECT_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/disconnect`;

/**
 * 🔒 The address Zoho sends the browser back to, which a person registers in
 * Zoho's console. The proxy serves the API under `/api` and strips the prefix
 * before the API sees the request, so the callback route answers it.
 */
export function zohoBooksRedirectUri(appBaseUrl: string): string {
  return `${appBaseUrl.replace(/\/+$/, '')}/api${ZOHO_BOOKS_CALLBACK_PATH}`;
}

/** Zoho's authorization address for the data centre's sign-in host, with the seventeen scopes, offline access and consent asked every time. */
export function zohoBooksAuthorizationUrl(input: { dataCentre: ZohoBooksDataCentre; clientId: string; redirectUri: string; state: string }): string {
  const query = new URLSearchParams({
    scope: ZOHO_BOOKS_SCOPES.join(','),
    client_id: input.clientId,
    response_type: 'code',
    access_type: 'offline',
    prompt: 'consent',
    redirect_uri: input.redirectUri,
    state: input.state,
  });
  return `${zohoBooksHosts(input.dataCentre).accounts}/oauth/v2/auth?${query.toString()}`;
}

/**
 * The data centre Zoho's Multi-DC answer names in `location`. ⚠️ Zoho's Books
 * page shows `eu` alone; the other six codes are Zoho's usual region names and
 * are unproved until a connection from that region returns (piece 31, Open issues).
 */
const LOCATION_DATA_CENTRES: Readonly<Record<string, ZohoBooksDataCentre>> = {
  us: 'com',
  com: 'com',
  eu: 'eu',
  in: 'in',
  au: 'com.au',
  'com.au': 'com.au',
  jp: 'jp',
  ca: 'ca',
  sa: 'sa',
};

export function zohoBooksDataCentreFromLocation(location: unknown): ZohoBooksDataCentre | null {
  if (typeof location !== 'string') return null;
  const key = location.trim().toLowerCase();
  const mapped = LOCATION_DATA_CENTRES[key];
  if (mapped) return mapped;
  return isZohoBooksDataCentre(key) ? key : null;
}

/**
 * 🔒 The query the callback returns to Settings with: `connected`, or `refused`
 * with a reason code in `reason` and, for four reasons, Zoho's own word in
 * `word`. 🔨 No sentence travels in the address: the page looks the sentence up
 * from its own fixed list, so a crafted link can put no text of its choosing in
 * front of an administrator beyond one short word (decision 1465).
 */
export const ZOHO_BOOKS_OUTCOME_PARAM = 'zohoBooks';
export const ZOHO_BOOKS_OUTCOME_REASON_PARAM = 'reason';
export const ZOHO_BOOKS_OUTCOME_WORD_PARAM = 'word';
export type ZohoBooksConnectOutcome = 'connected' | 'refused';

/** Every reason the callback refuses for, each one a fixed sentence on the page. */
export const ZOHO_BOOKS_REFUSALS = [
  'noState',
  'stateUnknown',
  'stateLapsed',
  'otherSignIn',
  'zohoDenied',
  'noCode',
  'unknownLocation',
  'noDataCentre',
  'noClient',
  'unreachable',
  'exchangeRefused',
  'exchangeStatus',
  'noRefreshToken',
] as const;
export type ZohoBooksRefusal = (typeof ZOHO_BOOKS_REFUSALS)[number];

/** The four reasons that carry Zoho's own word: its `error`, the `location` it named, the exchange's error word, and the exchange's HTTP status. */
export const ZOHO_BOOKS_WORDED_REFUSALS: readonly ZohoBooksRefusal[] = ['zohoDenied', 'unknownLocation', 'exchangeRefused', 'exchangeStatus'];

export function isZohoBooksRefusal(value: unknown): value is ZohoBooksRefusal {
  return typeof value === 'string' && (ZOHO_BOOKS_REFUSALS as readonly string[]).includes(value);
}

/** The longest word Zoho's answer may put on the page. */
export const ZOHO_BOOKS_WORD_LIMIT = 40;

/**
 * 🔒 Zoho's own word as the page may show it: one token of letters, digits,
 * underscores, dots and hyphens, at most forty characters, such as
 * `access_denied`, `com.au` or `500`. Anything else is null, and is dropped by
 * the callback and refused by the page, never trimmed into shape.
 */
export function zohoBooksOutcomeWord(value: unknown): string | null {
  return typeof value === 'string' && new RegExp(`^[A-Za-z0-9_.-]{1,${ZOHO_BOOKS_WORD_LIMIT}}$`).test(value) ? value : null;
}

/** The Settings page's address with the outcome on it, where the callback sends the browser; a word that fails the filter is left off. */
export function zohoBooksSettingsReturn(appBaseUrl: string, outcome: 'connected'): string;
export function zohoBooksSettingsReturn(appBaseUrl: string, outcome: 'refused', reason: ZohoBooksRefusal, word?: string | null): string;
export function zohoBooksSettingsReturn(appBaseUrl: string, outcome: ZohoBooksConnectOutcome, reason: ZohoBooksRefusal | null = null, word: string | null = null): string {
  const query = new URLSearchParams({ [ZOHO_BOOKS_OUTCOME_PARAM]: outcome });
  if (outcome === 'refused' && reason) {
    query.set(ZOHO_BOOKS_OUTCOME_REASON_PARAM, reason);
    const shown = ZOHO_BOOKS_WORDED_REFUSALS.includes(reason) ? zohoBooksOutcomeWord(word) : null;
    if (shown) query.set(ZOHO_BOOKS_OUTCOME_WORD_PARAM, shown);
  }
  return `${appBaseUrl.replace(/\/+$/, '')}${ZOHO_BOOKS_SETTINGS_ROUTE}?${query.toString()}`;
}

/** What the connect route answers: the address to send the browser to, and nothing else. */
export interface ZohoBooksConnectAnswer {
  authorizationUrl: string;
}

/** What Disconnect answers: true where a token was held and Zoho did not confirm its revoke, so the page can say so. */
export interface ZohoBooksDisconnectAnswer {
  revokeUnconfirmed: boolean;
}

/**
 * 🔒 How Settings reads the refresh token back (decision 1465): held or not,
 * with no tail, since nobody typed it, and the time it was written.
 */
export interface ZohoBooksConnectionView extends SealedValueView {
  connectedAt: string | null;
}

export function isZohoBooksConnectionView(value: unknown): value is ZohoBooksConnectionView {
  return typeof value === 'object' && value !== null && (value as ZohoBooksConnectionView).sealed === true;
}
